// src/platform/detect.js
//
// Single source of truth for "which platform UI to render?" Decision is
// computed once per page load and memoised — pages and components should
// NOT call this in a render loop; they should branch at boot time.
//
// Documented in: zd-agent-skills/frontend/design-system/01-platform-detection.md

let _isMobile;

/**
 * Returns true if the current runtime should render the mobile UI.
 *
 * Heuristic — four signals in priority order:
 *   1. Capacitor native shell (iOS / Android app installs) — definitive
 *   2. Touch-first device (coarse pointer + no hover) — phones, most tablets
 *   3. Everything else → web UI (desktop browsers + touchscreen laptops with mouse/touchpad)
 */
export function isMobile() {
  if (_isMobile !== undefined) return _isMobile;

  // 1. Capacitor native shell
  if (globalThis.Capacitor?.isNativePlatform?.()) {
    _isMobile = true;
    return _isMobile;
  }

  // 2. Touch-first browser
  const coarse = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;
  const noHover = typeof window !== 'undefined' && window.matchMedia?.('(hover: none)').matches;

  _isMobile = !!(coarse && noHover);
  return _isMobile;
}

/**
 * Returns true if the current runtime should render the web UI.
 *
 * Always the inverse of `isMobile()` — the two are mutually exclusive.
 */
export function isWeb() {
  return !isMobile();
}

/**
 * Test-only — clears the memoised value so unit tests can re-evaluate with
 * different stubbed signals. Production code never calls this.
 */
export function _resetPlatformCache() {
  _isMobile = undefined;
}
